'use client';

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { JWPlayer, JWPlayerTimeEvent } from '@/types/jwplayer';
import updateUserActivity from '@/lib/api/update-user-activity';
import { VideoData } from '@/types/course';
import { useAuthUser } from '@/lib/auth/common/useSession';
import { Lesson, UserCourse } from '@/types/user-course-lessons';
import VideoTransitionScreen from './video-transition-screen';
import CourseCompletedScreen from './course-completed-screen';
import { useSurveyStore } from '@/store/store';

interface TranscriptionCaptions {
  subtitles?: string;
  captions?: string;
  vtt?: string;
  subtitle?: string;
  transcript?: string;
}

interface JWCloudPlayerProps {
  isLessonVideo?: boolean;
  thumbnail?: string;
  className?: string;
  transcriptions_captions?: TranscriptionCaptions | null;
  courseEntryUid: string;
  lessonEntryUid?: string;
  userCourseData: UserCourse | null;
  userLessonData: Lesson | null;
  currentVideoData: VideoData;
  onVideoComplete?: () => void;
  nextLessonTitle?: string;
  courseName?: string;
  onPlayStateChange?: (isPlaying: boolean) => void;
  onPlayerReady?: (controls: { play: () => void; pause: () => void }) => void;
  /** Whether the video should start playing automatically when loaded. Defaults to true. */
  autostart?: boolean;
}

// Helper function to create subtitle track
const createSubtitleTrack = (captionsData: TranscriptionCaptions | null | undefined): { file: string; label: string; kind: string; default: boolean }[] => {
  if (!captionsData) return [];
  
  const vttData = (captionsData as any).subtitles || 
                  (captionsData as any).captions || 
                  (captionsData as any).vtt || 
                  (captionsData as any).subtitle;
  
  if (vttData && typeof vttData === 'string') {
    const blob = new Blob([vttData], { type: 'text/vtt' });
    const blobUrl = URL.createObjectURL(blob);
    return [{
      file: blobUrl,
      label: 'English',
      kind: 'captions',
      default: true,
    }];
  }
  
  return [];
};

// Helper function to seek to saved position
const seekToSavedPosition = (
  player: JWPlayer,
  percentage: number,
  onComplete: () => void
) => {
  const duration = player.getDuration();
  
  if (duration > 0) {
    const seekTo = Math.floor((percentage / 100) * duration);
    player.seek(seekTo);
    onComplete();
  } else {
    // Wait for duration to be available
    const checkDuration = setInterval(() => {
      const newDuration = player.getDuration();
      if (newDuration > 0) {
        clearInterval(checkDuration);
        const seekTo = Math.floor((percentage / 100) * newDuration);
        player.seek(seekTo);
        onComplete();
      }
    }, 100);
    
    setTimeout(() => clearInterval(checkDuration), 5000);
  }
};

export default function VideoPlayer({
  isLessonVideo = false,
  thumbnail,
  className = '',
  transcriptions_captions,
  courseEntryUid,
  lessonEntryUid,
  currentVideoData,
  userCourseData,
  userLessonData,
  onVideoComplete,
  nextLessonTitle,
  courseName,
  onPlayStateChange,
  onPlayerReady,
  autostart = true,
}: JWCloudPlayerProps) {
  const queryClient = useQueryClient();
  const { isLoggedIn, isLoading: authLoading } = useAuthUser();
  const { showSurvey, setShowSurvey } = useSurveyStore();
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerInstanceRef = useRef<JWPlayer | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const hasSeekedRef = useRef(false);

  const [videoData, setVideoData] = useState(currentVideoData);
  const [showTransition, setShowTransition] = useState(false);
  const [showCourseCompleted, setShowCourseCompleted] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  
  const userProgress = isLessonVideo ? userLessonData : userCourseData;
  const isAlreadyCompleted = userProgress?.progress?.isCompleted === true;

  // 1. CLIENT-SIDE ONLY: Prevent SSR hydration issues
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Sync completion overlay: when survey is closed (e.g. via X), hide completion overlay (academy-1.0 behavior)
  useEffect(() => {
    if (!showSurvey) {
      setShowCourseCompleted(false);
    }
  }, [showSurvey]);

  // 2a. Clean up player when playback error is shown
  useEffect(() => {
    if (playbackError && playerInstanceRef.current) {
      try {
        playerInstanceRef.current.remove();
      } catch {
        // Silently fail
      }
      playerInstanceRef.current = null;
    }
  }, [playbackError]);

  // 2. VIDEO SWITCHING: Reset state when navigating between lessons
  useEffect(() => {
    if (authLoading) return; // no verdict yet — wait rather than act on "not logged in"
    if (isLoggedIn) {
      setVideoData(currentVideoData);
      setPlaybackError(null);
      onPlayStateChange?.(false);
      hasSeekedRef.current = false;
      setIsPlayerReady(false);
    }
  }, [currentVideoData, onPlayStateChange, isLoggedIn, authLoading]);

  // 3. PROGRESS RESUMPTION: Seek to saved position when player + data are ready
  useEffect(() => {
    if (!isPlayerReady || hasSeekedRef.current || !playerInstanceRef.current || !userProgress) {
      return;
    }

    const percentage = userProgress.progress?.percentage;
    
    if (percentage && percentage > 0 && !isAlreadyCompleted) {
      seekToSavedPosition(
        playerInstanceRef.current,
        percentage,
        () => { hasSeekedRef.current = true; }
      );
    }
  }, [isPlayerReady, userProgress, isAlreadyCompleted]);

  // 4. PLAYER INITIALIZATION: Setup JWPlayer with event handlers
  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const videoFile = videoData?.sources?.[0]?.file;
    const hasValidSource = videoFile && typeof videoFile === 'string' && videoFile.trim() !== '';

    if (!hasValidSource) {
      setPlaybackError('This video cannot be played. The video source is missing or invalid.');
      return;
    }

    setPlaybackError(null);
    const containerId = `jwplayer-${Math.random().toString(36).substr(2, 9)}`;

    if (playerContainerRef.current) {
      playerContainerRef.current.id = containerId;
    } else {
      console.error('Player container ref not available');
      return;
    }

    const loadPlayer = () => {
      const script = document.createElement('script');
      script.src = `https://cdn.jwplayer.com/libraries/b769H14u.js`;
      script.async = true;
      

      script.onload = () => {
        if (!window.jwplayer) return;
        
        const containerElement = document.getElementById(containerId);
        if (!containerElement) {
          console.error('Player container element not found in DOM');
          return;
        }
        
        playerInstanceRef.current = window.jwplayer(containerId);
        
        if (!playerInstanceRef.current) {
          console.error('Failed to initialize JW Player');
          return;
        }
        
        const subtitleTrack = createSubtitleTrack(transcriptions_captions);
        if (subtitleTrack.length > 0) {
          blobUrlRef.current = subtitleTrack[0].file;
        }

        playerInstanceRef.current.setup({
          // "playlist": playlist,
          file: videoData?.sources[0]?.file,
          image: thumbnail || '',
          title: videoData?.title || '',
          aspectratio: '16:9',
          autoPause: {
            viewability: false,
          },
          autostart,
          tracks: subtitleTrack,
          captions: {
            backgroundColor: '#000000',
            backgroundOpacity: 75,
            color: '#FFFFFF',
            edgeStyle: 'none',
            fontFamily: 'Inter',
            fontOpacity: 100,
            fontSize: 12,
            windowColor: '#000000',
            windowOpacity: 0,
            padding: 10,
          },
          controls: true,
          playbackRateControls: true,
          playbackRates: [0.5, 0.25, 1, 1.5, 1.75, 2],
          displayHeading: false,
          displaydescription: false,
          displaytitle: false,
          floating: {
            mode: 'never',
          },
          generateSEOMetadata: true,
          include_compatibility_script: false,
          interactive: false,
          intl: {
            en: {
              sharing: {
                heading: 'Share Video',
              },
            },
          },
          logo: {
            hide: false,
            position: 'top-right',
          },
          mute: false,
          pipIcon: 'disabled',
          preload: 'metadata',
          repeat: false,
          sharing: true,
          sharingConfig: {
            link: window.location.toString(),
            heading: 'Share This Video',
            sites: ['facebook', 'twitter', 'linkedin', 'email'],
          },
          stretching: 'uniform',
          width: '100%',
          previewThumbnails: true,
        });

        // Mark player as ready when ready event fires
        playerInstanceRef.current.on('ready', () => {
          setIsPlayerReady(true);
          
          // Expose play/pause controls to parent
          if (onPlayerReady && playerInstanceRef.current) {
            onPlayerReady({
              play: () => playerInstanceRef.current?.play(),
              pause: () => playerInstanceRef.current?.pause(),
            });
          }
        });

        // Mark lesson as started when first frame plays
        playerInstanceRef.current.on('firstFrame', async () => {
          if (!isAlreadyCompleted) {
            await updateUserActivity({
              isLessonVideo,
              lessonEntryUid,
              lessonActivity: { progress: { hasStarted: true } },
              courseEntryUid,
              courseActivity: isLessonVideo ? null : { progress: { hasStarted: true } },
              queryClient,
            });
          }
        });

        // Track progress at 10% intervals
        let lastUpdatePosition = 0;
        let updateInterval = 0;
        
        playerInstanceRef.current.on('time', async (e) => {
          const { duration, position } = e as JWPlayerTimeEvent;
          
          if (updateInterval === 0 && duration > 0) {
            updateInterval = duration / 10;
            lastUpdatePosition = position;
          }

          if (position - lastUpdatePosition >= updateInterval && !isAlreadyCompleted) {
            lastUpdatePosition = position;
            const currentPercentage = (position / duration) * 100;

            await updateUserActivity({
              isLessonVideo,
              lessonEntryUid,
              lessonActivity: { progress: { percentage: currentPercentage } },
              courseEntryUid,
              courseActivity: isLessonVideo ? null : { progress: { percentage: currentPercentage } },
              queryClient,
            });
          }
        });

        playerInstanceRef.current.on('complete', async () => {
          onPlayStateChange?.(false);

          if (!isAlreadyCompleted) {
            await updateUserActivity({
              isLessonVideo,
              lessonEntryUid,
              lessonActivity: { progress: { isCompleted: true, percentage: 100 } },
              courseEntryUid,
              courseActivity: isLessonVideo ? null : { progress: { isCompleted: true, percentage: 100 } },
              queryClient,
            });
          }

          const isLastLesson = !nextLessonTitle && isLessonVideo;

          if (isLastLesson) {
            setShowSurvey(true); // Open survey modal (academy-1.0 behavior)
            setShowCourseCompleted(true);
          } else if (nextLessonTitle) {
            setShowTransition(true);
          } else {
            onVideoComplete?.();
          }
        });

        // Track play/pause state for sidebar sync
        playerInstanceRef.current.on('play', () => onPlayStateChange?.(true));
        playerInstanceRef.current.on('pause', () => onPlayStateChange?.(false));

        // Handle playback errors (e.g. 102630: "This Video File Cannot Be Played")
        playerInstanceRef.current.on('error', (e: unknown) => {
          const ev = e as { code?: number; message?: string };
          const code = ev.code;
          const msg = ev.message;
          if (code === 102630 || msg?.toLowerCase().includes('cannot be played')) {
            setPlaybackError('This video cannot be played. The file may be unavailable or in an unsupported format.');
          } else {
            setPlaybackError(msg || 'An error occurred while playing the video.');
          }
        });
      };

      document.body.appendChild(script);
    };

    loadPlayer();

    return () => {
      onPlayStateChange?.(false);
      setIsPlayerReady(false);

      if (playerInstanceRef.current) {
        try {
          playerInstanceRef.current.remove();
        } catch {
          // Silently fail if player removal errors
        }
      }

      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [videoData, transcriptions_captions, onVideoComplete, isMounted, onPlayStateChange, onPlayerReady, autostart]);

  const handleTransition = () => {
    setShowTransition(false);
    onVideoComplete?.();
  };

  if (playbackError) {
    return (
      <div className="video-player-container aspect-video w-full bg-black relative flex flex-col items-center justify-center p-6">
        <div className="flex flex-col items-center justify-center gap-4 text-center max-w-md">
          {thumbnail && (
            <div className="relative w-full max-w-[320px] aspect-video rounded-lg overflow-hidden opacity-60">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={thumbnail}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <p className="cs-m-body text-text-default-medium">{playbackError}</p>
          <p className="cs-caption text-text-default-light">
            Try refreshing the page or contact support if the issue persists.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="video-player-container aspect-video w-full bg-black relative">
      <div ref={playerContainerRef} className={`jw-player-container ${className}`} />
      
      {showTransition && nextLessonTitle && (
        <VideoTransitionScreen
          nextLessonTitle={nextLessonTitle}
          onCountdownComplete={handleTransition}
          onSkip={handleTransition}
        />
      )}

      {showCourseCompleted && (
        <CourseCompletedScreen
          title={courseName || 'this course'}
          completionMessage="You have completed"
          secondaryAction={{
            label: 'Explore related',
            onClick: () => {
              setShowSurvey(false);
              setShowCourseCompleted(false);
              window.location.href = '/academy/catalog?q=' + courseName;
            },
            variant: 'SecondaryFillButton',
          }}
          primaryAction={{
            label: 'Provide feedback',
            onClick: () => {
              setShowCourseCompleted(false);
            },
            variant: 'PrimaryFillButton',
          }}
          enableFeedbackNavigation={false}
          showGlow={true}
        />
      )}
    </div>
  );
}
